import axios from "axios";

const ROBOFLOW_API_URL = "https://detect.roboflow.com/drinking-detection-1roab/3";
const ROBOFLOW_API_KEY = import.meta.env.VITE_ROBOFLOW_API_KEY || process.env.REACT_APP_ROBOFLOW_API_KEY || "EpIzu5XILEYitZlr1CJb";

/**
 * Detects drinking action from a base64 encoded image using Roboflow API
 * @param imageBase64 - Base64 encoded image data (without the data:image prefix)
 * @returns Promise with detection results
 */
export const detectDrinkingFromBase64 = async (imageBase64: string) => {
  try {
    const response = await axios({
      method: "POST",
      url: ROBOFLOW_API_URL,
      params: {
        api_key: ROBOFLOW_API_KEY
      },
      data: imageBase64,
      headers: {
        "Content-Type": "application/x-www-form-urlencoded"
      }
    });
    return response.data;
  } catch (error) {
    console.error("Roboflow API error:", error);
    throw error;
  }
};

/**
 * Detects drinking action from an image URL using Roboflow API
 * @param imageUrl - URL of the image to analyze
 * @returns Promise with detection results
 */
export const detectDrinkingFromUrl = async (imageUrl: string) => {
  try {
    const response = await axios({
      method: "POST",
      url: ROBOFLOW_API_URL,
      params: {
        api_key: ROBOFLOW_API_KEY,
        image: imageUrl
      }
    });
    return response.data;
  } catch (error) {
    console.error("Roboflow API error:", error);
    throw error;
  }
};

/**
 * Checks if the detection results indicate a drinking action
 * @param detectionResults - Results from Roboflow API
 * @returns Boolean indicating if drinking was detected
 */
export const isDrinkingDetected = (detectionResults: any): boolean => {
  // Check if there are any predictions
  if (!detectionResults.predictions || detectionResults.predictions.length === 0) {
    console.log("Roboflow: No predictions found.");
    return false;
  }

  // Look for "drinking" class with confidence above a higher threshold
  const DRINKING_CONFIDENCE_THRESHOLD = 0.3; // Increased to 40% for higher accuracy
  const drinkingPrediction = detectionResults.predictions.find(
    (prediction: any) => prediction.class === "Drinking"
  );

  if (drinkingPrediction) {
    const confidence = drinkingPrediction.confidence;
    console.log(`Roboflow: "Drinking" class detected with ${Math.round(confidence * 100)}% confidence.`);
    
    if (confidence >= DRINKING_CONFIDENCE_THRESHOLD) {
      console.log("Roboflow: Confidence is above threshold. Drinking detected!");
      return true;
    } else {
      console.log("Roboflow: Confidence is below threshold. Not considered a drink.");
      return false;
    }
  }

  console.log("Roboflow: No 'drinking' class found in predictions.");
  return false;
};

/**
 * Checks if two bounding boxes overlap
 * @param box1 - First bounding box with x, y, width, height
 * @param box2 - Second bounding box with x, y, width, height
 * @returns Boolean indicating if the boxes overlap
 */
const isOverlapping = (box1: any, box2: any): boolean => {
  // Calculate the edges of the boxes
  const box1Right = box1.x + box1.width;
  const box1Bottom = box1.y + box1.height;
  const box2Right = box2.x + box2.width;
  const box2Bottom = box2.y + box2.height;

  // Check for overlap
  return !(
    box2.x > box1Right ||
    box2Right < box1.x ||
    box2.y > box1Bottom ||
    box2Bottom < box1.y
  );
};

/**
 * Captures the current frame from a video element as a base64 string
 * @param videoElement - HTML Video Element to capture from
 * @returns Base64 encoded image (without the data:image prefix)
 */
export const captureVideoFrame = (videoElement: HTMLVideoElement): string => {
  const canvas = document.createElement('canvas');
  canvas.width = videoElement.videoWidth;
  canvas.height = videoElement.videoHeight;

  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error("Could not get canvas context");

  ctx.drawImage(videoElement, 0, 0, canvas.width, canvas.height);

  // Get base64 string (remove the data:image/png;base64, prefix)
  const base64Image = canvas.toDataURL('image/jpeg').split(',')[1];
  return base64Image;
};
